import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  createMercadoPagoCheckout,
  createOrder,
  getEtaPreview,
  getPublicMenu,
  getStoreStatus,
  validatePromoCode,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { reconcileCart, formatLineLabel } from '../lib/cartUtils';
import { cartTotals } from '../lib/pizzaPricing';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { closedNoticeText, closedButtonLabel, isStoreClosed } from '../lib/storeHoursDisplay';
import { Clock, Check } from 'lucide-react';
import AnimatedPrice from '../components/pizzeria/AnimatedPrice';
import {
  CURRENT_NOTICE_VERSION,
  legalValue,
  renderLegalHtml,
  useLegalIdentity,
} from '../lib/legalIdentity';

/** Aviso de privacidad simplificado (doc. 02 §A). Los {{…}} salen de «Identidad y contacto». */
const SIMPLIFIED_NOTICE_HTML =
  '<strong>Aviso de privacidad simplificado.</strong> {{responsable_nombre}} ({{nombre_comercial}}), con domicilio en {{domicilio}}, ' +
  'es responsable de los datos que nos da en este pedido: nombre, teléfono con WhatsApp, dirección y referencias, y, si paga por ' +
  'transferencia, su comprobante. Los usamos para tomar, cobrar y entregar su pedido y avisarle por WhatsApp de su estado. Si lo ' +
  'autoriza, también para enviarle promociones por WhatsApp; si acepta las cookies de analítica, para medir el uso del sitio. Puede ' +
  'negarse a las promociones dejando la casilla sin marcar o respondiendo <strong>BAJA</strong>, y a la analítica en el aviso de ' +
  'cookies. <strong>Este es un sitio de demostración: puede usar datos de prueba.</strong> Aviso integral y derechos ARCO: ' +
  '{{page:/aviso-de-privacidad}} · {{correo_privacidad}}';

const PRIVACY_REQUIRED_MSG = 'Para continuar, acepte el aviso de privacidad y los términos.';
const PREPAY_REQUIRED_MSG = 'Para pagar ahora, autorice el cobro anticipado o elija pago contra entrega.';

function money2(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';

/** Pasos visibles (solo visual): Carrito → Datos y entrega → Pago */
function CheckoutSteps() {
  const steps = ['Carrito', 'Datos y entrega', 'Pago'];
  return (
    <ol className="flex items-center gap-2 mb-6 text-sm font-semibold" aria-label="Pasos del pedido">
      {steps.map((s, i) => (
        <li key={s} className="flex items-center gap-2 flex-1 last:flex-none">
          <span
            className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center border-2 ${
              i === 0 ? 'bg-[#3F7D3A] border-[#3F7D3A] text-white' : i === 1 ? 'bg-[#C2410C] border-[#C2410C] text-white' : 'border-[#F1DFC8] text-[#6B5444]'
            }`}
            aria-current={i === 1 ? 'step' : undefined}
          >
            {i === 0 ? <Check className="w-4 h-4" aria-hidden="true" /> : i + 1}
          </span>
          <span className={i === 1 ? 'text-[#2B1A12]' : 'text-[#6B5444]'}>{s}</span>
          {i < steps.length - 1 ? <span className="flex-1 h-0.5 bg-[#F1DFC8] rounded" aria-hidden="true" /> : null}
        </li>
      ))}
    </ol>
  );
}

function readSavedCart() {
  try {
    const saved = sessionStorage.getItem('pizas_cart');
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function CheckoutPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const cartFromState = state?.cart;
  const { schedule } = useClinicBranding();
  // Carrito inicial síncrono (state o sessionStorage). Antes empezaba vacío y el efecto
  // de abajo redirigía a /pedir antes de leer sessionStorage («Ir a pagar» rebotaba al menú).
  const [cart, setCart] = useState(() =>
    Array.isArray(cartFromState) && cartFromState.length ? cartFromState : readSavedCart(),
  );
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [menuCategories, setMenuCategories] = useState([]);
  const [store, setStore] = useState(null);
  const [storeClosed, setStoreClosed] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [addressRef, setAddressRef] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mercadopago');
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [eta, setEta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  // Paquete legal v2026-10-09: casillas sin marcar por defecto (doc. 02 §B)
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentPrepay, setConsentPrepay] = useState(false);
  const { identity } = useLegalIdentity();
  const noticeVersion = legalValue(identity, 'notice_version') || CURRENT_NOTICE_VERSION;

  useEffect(() => {
    const raw = Array.isArray(cartFromState) && cartFromState.length ? cartFromState : readSavedCart();
    getPublicMenu()
      .then((data) => {
        setStore(data.store || null);
        setMenuCategories(data.categories || []);
        const { cart: synced, removed } = reconcileCart(
          raw,
          data.categories,
          data.topping_extras || [],
        );
        if (removed.length) {
          toast.warning(
            `Productos ya no disponibles y se quitaron del carrito: ${removed.join(', ')}. Vuelve al menú si quieres pedir de nuevo.`,
            { duration: 10000 },
          );
        }
        setCart(synced);
        sessionStorage.setItem('pizas_cart', JSON.stringify(synced));
        if (data.store?.mercadopago_enabled) {
          setPaymentMethod('mercadopago');
        } else {
          setPaymentMethod('transfer');
        }
      })
      .catch(() => {})
      .finally(() => setMenuLoaded(true));
    getStoreStatus()
      .then(setStoreClosed)
      .catch(() => {});
  }, [cartFromState]);

  useEffect(() => {
    getEtaPreview().then(setEta).catch(() => {});
  }, []);

  const closed = isStoreClosed(storeClosed);
  const closedNotice = closedNoticeText(storeClosed, schedule);

  const totals = cartTotals(cart, deliveryType === 'pickup' ? 0 : Number(store?.delivery_fee || 0));
  const subtotal = totals.subtotal;
  const deliveryFee = totals.delivery_fee;
  const autoDiscount = totals.auto_discount;
  // Cupón solo si no hay descuento auto de carta
  const promoDiscount =
    autoDiscount > 0 ? 0 : promoApplied ? Number(promoApplied.discount || 0) : 0;
  const total = Math.max(0, subtotal + deliveryFee - autoDiscount - promoDiscount);
  const mpEnabled = Boolean(store?.mercadopago_enabled);
  const mpSandbox = store?.mercadopago_sandbox !== false;
  const legalTerms = store?.legal_terms || {};
  const prepaid = paymentMethod === 'mercadopago' || paymentMethod === 'transfer';
  const brand = legalValue(identity, 'store_brand') || 'Pizzas';
  const tradeName = legalValue(identity, 'trade_name') || 'DanySolutions';
  const simplifiedNotice = useMemo(
    () => renderLegalHtml(SIMPLIFIED_NOTICE_HTML, { identity }),
    [identity],
  );
  // El total cambia (cupón, entrega…): la autorización de cobro se vuelve a pedir.
  useEffect(() => {
    setConsentPrepay(false);
  }, [paymentMethod, total]);

  const notesText = useMemo(() => {
    const parts = [];
    if (addressRef.trim()) parts.push(`Referencias: ${addressRef.trim()}`);
    return parts.join('\n');
  }, [addressRef]);

  const submit = async (e) => {
    e.preventDefault();
    if (closed) {
      toast.error(closedNotice || 'Estamos cerrados en este momento.');
      return;
    }
    if (!name.trim() || phone.replace(/\D/g, '').length < 10) {
      toast.error('Completa nombre y teléfono');
      return;
    }
    if (deliveryType === 'delivery' && address.trim().length < 10) {
      toast.error('Completa la dirección');
      return;
    }
    if (!acceptPrivacy) {
      toast.error(PRIVACY_REQUIRED_MSG);
      document.getElementById('consent-privacy')?.focus();
      return;
    }
    if (prepaid && !consentPrepay) {
      toast.error(PREPAY_REQUIRED_MSG);
      document.getElementById('consent-prepay')?.focus();
      return;
    }
    if (paymentMethod === 'mercadopago' && !mpEnabled) {
      toast.error('Mercado Pago no está configurado. Usa transferencia o pide al admin que lo active en Config tienda.');
      return;
    }
    const { cart: syncedCart, removed } = reconcileCart(cart, menuCategories);
    if (removed.length) {
      setCart(syncedCart);
      sessionStorage.setItem('pizas_cart', JSON.stringify(syncedCart));
      toast.error(
        `Algunos productos ya no están en el menú (${removed.join(', ')}). Revisa tu carrito.`,
      );
      return;
    }
    if (!syncedCart.length) {
      toast.error('Tu carrito quedó vacío. Agrega productos desde el menú.');
      navigate('/pedir', { replace: true });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        delivery_address: deliveryType === 'pickup' ? 'Recoger en sucursal' : address.trim(),
        lines: syncedCart.map((c) => ({
          menu_item_id: c.menu_item_id,
          qty: c.qty,
          notes: c.notes || '',
          size_key: c.size_key || '',
          half_specialty_id: c.half_specialty_id || '',
          extra_topping_ids: c.extra_topping_ids || (c.extra_toppings || []).map((t) => t.id),
        })),
        notes: notesText,
        delivery_type: deliveryType,
        promo_code: autoDiscount > 0 ? '' : promoApplied?.code || '',
        // Paquete legal v2026-10-09: el backend valida y guarda el consentimiento (colección consents)
        accepted_privacy_terms: acceptPrivacy,
        consent_marketing_whatsapp: consentMarketing,
        consent_prepayment: prepaid ? consentPrepay : false,
        privacy_notice_version: noticeVersion,
      };
      if (paymentMethod === 'mercadopago') {
        const ch = await createMercadoPagoCheckout(payload);
        const url = ch.init_point || '';
        if (!url.startsWith('http')) {
          toast.error(
            'No se pudo abrir Mercado Pago. El administrador debe configurar Access Token en Config tienda → Mercado Pago.'
          );
          return;
        }
        sessionStorage.setItem('pizas_cart_pending_checkout', ch.checkout_id);
        window.location.href = url;
        return;
      }
      const pm = paymentMethod === 'cash_on_delivery' ? 'cash_on_delivery' : 'transfer';
      const order = await createOrder({ ...payload, payment_method: pm });
      sessionStorage.removeItem('pizas_cart');
      navigate(`/pagar/${order.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al crear el pedido');
    } finally {
      setSubmitting(false);
    }
  };

  if (menuLoaded && !cart.length) {
    return (
      <div className="container mx-auto px-4 py-10 max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>Tu carrito está vacío</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Agrega tus pizzas favoritas desde el menú.</p>
            <Button asChild className="rounded-full">
              <Link to="/pedir">Ver menú</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={PIZAS_MODE ? 't-paper' : ''}>
    <div className="container mx-auto px-4 py-10 max-w-lg">
      <Card className={PIZAS_MODE ? 't-card shadow-xl' : ''}>
        <CardHeader>
          <CardTitle className={PIZAS_MODE ? 'font-heading text-3xl font-extrabold' : ''}>
            {PIZAS_MODE ? 'Tu pedido' : 'Checkout'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {PIZAS_MODE ? <CheckoutSteps /> : null}
          <p className="text-sm bg-[#FFF6EA] border border-[#F2B33D] rounded-md p-3 mb-4" data-testid="checkout-demo-notice">
            <strong>Sitio de demostración:</strong> puede usar un nombre y dirección de prueba.{' '}
            {mpSandbox ? 'Mercado Pago está en modo prueba: no se realizan cargos reales.' : null}
          </p>
          {closed ? (
            <div
              role="alert"
              className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-md p-3 mb-4 flex items-start gap-2"
            >
              <Clock className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-semibold">{closedNotice}</p>
                <p>
                  Puedes revisar tu pedido; podrás confirmarlo cuando abramos.{' '}
                  <Link to="/pedir" className="underline font-medium">
                    Volver al menú
                  </Link>
                </p>
              </div>
            </div>
          ) : null}
          {!mpEnabled ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
              Mercado Pago no está activo. El administrador debe configurarlo en el panel:{' '}
              <strong>Config tienda → Mercado Pago</strong>.
            </p>
          ) : null}
          <ul className="mb-4 text-sm space-y-1">
            {cart.map((c, i) => (
              <li key={`${c.menu_item_id}-${i}`}>
                {c.qty}x {formatLineLabel(c)} — ${c.line_total.toFixed(0)}
              </li>
            ))}
          </ul>
          <div className="mb-4 text-sm space-y-1" data-testid="checkout-summary">
            <p>Subtotal: {money2(subtotal)} MXN</p>
            {/* El envío se muestra siempre, aunque sea gratis (doc. 02 §B, doc. 05 §5) */}
            <p data-testid="checkout-shipping">
              Envío:{' '}
              {deliveryType === 'pickup'
                ? 'no aplica (recoge en sucursal)'
                : deliveryFee > 0
                  ? `${money2(deliveryFee)} MXN`
                  : '$0.00 MXN (gratis)'}
            </p>
            {autoDiscount > 0 ? (
              <p className="text-emerald-700">
                Promo carta ({totals.auto_discount_detail}): −${autoDiscount.toFixed(0)} MXN
              </p>
            ) : null}
            {promoDiscount > 0 ? (
              <p className="text-emerald-700">Descuento ({promoApplied?.code}): −${promoDiscount.toFixed(0)} MXN</p>
            ) : null}
            <p className="font-bold text-lg">
              Total a pagar: <AnimatedPrice value={Math.round(total)} suffix=" MXN" />{' '}
              <span className="text-sm font-semibold">(IVA y envío incluidos)</span>
            </p>
            {legalTerms.radio_entrega_km || legalTerms.tiempo_entrega ? (
              <p className="text-xs text-muted-foreground">
                {legalTerms.radio_entrega_km ? `Entregamos hasta ${legalTerms.radio_entrega_km} km de la sucursal. ` : ''}
                {legalTerms.tiempo_entrega ? `Tiempo estimado: ${legalTerms.tiempo_entrega}. ` : ''}
                {legalTerms.pedido_minimo ? `Pedido mínimo: ${legalTerms.pedido_minimo}.` : ''}
              </p>
            ) : null}
          </div>
          {paymentMethod === 'mercadopago' ? (
            <p className="text-xs text-muted-foreground mb-4">
              El pedido se registrará en cocina <strong>solo después</strong> de que Mercado Pago confirme tu pago.
            </p>
          ) : null}
          {paymentMethod === 'cash_on_delivery' ? (
            <p className="text-xs text-muted-foreground mb-4">
              Pagas al recibir. El local revisará tu pedido y te confirmará por WhatsApp antes de prepararlo.
            </p>
          ) : null}
          {eta && (
            <p className="text-sm text-muted-foreground mb-4">
              Tiempo estimado: ~{eta.eta_minutes} min ({eta.orders_ahead} pedidos por delante)
            </p>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <Label>Tipo de entrega</Label>
              <select
                className="w-full border rounded-md px-3 py-2"
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
              >
                <option value="delivery">Entrega a domicilio</option>
                <option value="pickup">Recoger en sucursal</option>
              </select>
            </div>
            <div>
              <Label htmlFor="phone">Teléfono (WhatsApp)</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} required />
            </div>
            {deliveryType === 'delivery' ? (
              <>
                <div>
                  <Label htmlFor="address">Dirección de entrega</Label>
                  <Textarea
                    id="address"
                    rows={3}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="addressRef">Referencias (opcional)</Label>
                  <Input
                    id="addressRef"
                    value={addressRef}
                    onChange={(e) => setAddressRef(e.target.value)}
                    placeholder="Ej. portón azul, entre calles…"
                  />
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground space-y-1 rounded-md border p-3 bg-muted/30">
                <p className="font-medium text-foreground">Recoger en sucursal</p>
                {store?.store_pickup_address ? <p>{store.store_pickup_address}</p> : null}
                {store?.store_pickup_maps_url ? (
                  <a
                    href={store.store_pickup_maps_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                  >
                    Ver en mapa
                  </a>
                ) : null}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="promo">Cupón de descuento (opcional)</Label>
              <div className="flex gap-2">
                <Input
                  id="promo"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="CÓDIGO"
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={promoLoading || !promoInput.trim()}
                  onClick={async () => {
                    setPromoLoading(true);
                    try {
                      const res = await validatePromoCode({
                        code: promoInput.trim(),
                        customer_phone: phone.trim(),
                        delivery_type: deliveryType,
                        lines: cart.map((c) => ({
                          menu_item_id: c.menu_item_id,
                          qty: c.qty,
                          notes: c.notes || '',
                        })),
                      });
                      setPromoApplied(res);
                      toast.success(`Cupón aplicado: −$${Number(res.discount).toFixed(0)} MXN`);
                    } catch (err) {
                      setPromoApplied(null);
                      const d = err?.response?.data?.detail;
                      toast.error(typeof d === 'string' ? d : 'Cupón no válido');
                    } finally {
                      setPromoLoading(false);
                    }
                  }}
                >
                  {promoLoading ? '…' : 'Aplicar'}
                </Button>
              </div>
              {promoApplied ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline"
                  onClick={() => {
                    setPromoApplied(null);
                    setPromoInput('');
                  }}
                >
                  Quitar cupón
                </button>
              ) : null}
            </div>
            <div>
              <Label>Método de pago</Label>
              <select
                className="w-full border rounded-md px-3 py-2"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {mpEnabled ? <option value="mercadopago">{mpSandbox ? 'Mercado Pago (modo prueba)' : 'Mercado Pago'}</option> : null}
                <option value="transfer">Transferencia SPEI</option>
                <option value="cash_on_delivery">Contra entrega</option>
              </select>
              {paymentMethod === 'transfer' ? (
                <p className="text-xs text-muted-foreground mt-2">
                  Verificaremos su transferencia antes de preparar el pedido. Los datos del comprobante se usan solo para
                  confirmar su pago. <strong>Sitio de demostración: no realice transferencias reales.</strong>
                </p>
              ) : null}
            </div>
            {/* Paquete legal v2026-10-09 (doc. 02 §A-B): aviso simplificado y casillas */}
            <div
              className="text-xs leading-relaxed rounded-md border border-[#F1DFC8] bg-[#FFFDF8] p-3 [&_a]:underline [&_a]:text-[#C2410C]"
              data-testid="simplified-privacy-notice"
              // Valores escapados en renderLegalHtml; el resto es texto fijo del paquete legal.
              dangerouslySetInnerHTML={{ __html: simplifiedNotice }}
            />
            <div className="space-y-3 text-sm">
              <label className="flex items-start gap-2 cursor-pointer" htmlFor="consent-privacy">
                <input
                  id="consent-privacy"
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#C2410C]"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  required
                  aria-required="true"
                  data-testid="consent-privacy"
                />
                <span>
                  He leído y acepto el{' '}
                  <Link to="/aviso-de-privacidad" target="_blank" className="underline text-[#C2410C]">
                    Aviso de privacidad
                  </Link>{' '}
                  y los{' '}
                  <Link to="/terminos" target="_blank" className="underline text-[#C2410C]">
                    Términos y condiciones de venta
                  </Link>
                  . <span aria-hidden="true">*</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" htmlFor="consent-marketing">
                <input
                  id="consent-marketing"
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#C2410C]"
                  checked={consentMarketing}
                  onChange={(e) => setConsentMarketing(e.target.checked)}
                  data-testid="consent-marketing"
                />
                <span>
                  Quiero recibir promociones y cupones de {brand} por WhatsApp. Puedo darme de baja cuando quiera
                  respondiendo BAJA.
                </span>
              </label>
              {prepaid ? (
                <label className="flex items-start gap-2 cursor-pointer" htmlFor="consent-prepay">
                  <input
                    id="consent-prepay"
                    type="checkbox"
                    className="mt-1 h-4 w-4 shrink-0 accent-[#C2410C]"
                    checked={consentPrepay}
                    onChange={(e) => setConsentPrepay(e.target.checked)}
                    required
                    aria-required="true"
                    data-testid="consent-prepay"
                  />
                  <span>
                    Autorizo que se me cobre ahora el total de <strong>{money2(total)} MXN</strong> antes de recibir mi
                    pedido. Si el pedido no se acepta, se cancela antes de prepararse o llega incompleto o equivocado, se
                    me devolverá lo pagado por el mismo medio de pago. <span aria-hidden="true">*</span>
                  </span>
                </label>
              ) : (
                <p className="text-sm" data-testid="cod-notice">
                  Pagará {money2(total)} MXN al recibir su pedido. Le confirmaremos por WhatsApp antes de prepararlo.
                </p>
              )}
            </div>
            <Button
              type="submit"
              className={closed ? 'w-full disabled:opacity-100 bg-muted text-foreground border border-border' : 'w-full'}
              disabled={submitting || closed}
            >
              {closed
                ? closedButtonLabel(storeClosed, schedule)
                : submitting
                ? 'Procesando…'
                : paymentMethod === 'mercadopago'
                  ? `Pagar ${money2(total)} MXN con Mercado Pago`
                  : paymentMethod === 'cash_on_delivery'
                    ? 'Confirmar pedido (pago contra entrega)'
                    : 'Confirmar pedido y pagar por transferencia'}
            </Button>
            {paymentMethod === 'mercadopago' ? (
              <p className="text-xs text-muted-foreground" data-testid="mp-legend">
                🔒 Será redirigido a Mercado Pago para pagar de forma segura. {tradeName} no recibe ni guarda los datos
                de su tarjeta.
                {mpSandbox ? <strong> Modo prueba: no se realizan cargos reales.</strong> : null}
              </p>
            ) : null}
          </form>
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
